import ScoreCircle from './ScoreCircle';
import { getScoreColor, getScoreLabel, getScoreRowClasses, occKey, occOf, normWordKey, REDO_BELOW } from '../lib/score';
import { useState } from 'react';
import { Trophy } from 'lucide-react';
import { PronunciationTips } from './PronunciationTips';
import { UpgradeDialog, NoScore, NoScoreCaption } from './ProUpgrade';

/**
 * End-of-session pronunciation summary: average score badge plus a per-phrase
 * breakdown and a "redo low scores" hint. Shared by Manual and Automatic; each
 * caller supplies its own outer card/wrapper.
 *
 * `results` is an array of { phrase, score, feedback, issues?, phone_tips?, stress_tips? }.
 *
 * `challengeDay` is the caller's `user.challenge_day` (1..30, or null/undefined
 * outside the challenge). Day 30 is the challenge's last day before the badge
 * clears itself on day 31, so any session finished on day 30, in either mode,
 * is the one worth marking. `challengeUserName` is `user.name`, for greeting
 * them by name; only the first name is used, worked out here so Manual and
 * Automatic don't each have to.
 */
export default function SessionSummary({ results, challengeDay, challengeUserName }) {
    const firstName = (challengeUserName || '').trim().split(/\s+/)[0] || '';
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    // Decided from the session itself rather than the plan: free is scored
    // in full now, so only a session that actually ran out needs the lock.
    const scoreLocked = results.some(r => r.locked);
    // Only scored attempts count towards the average. An attempt with no score
    // because the free allowance is used up is still a phrase practised, so it
    // is listed, but it must not pull the average towards zero.
    const scored = results.filter(r => r.score != null);
    const unscored = results.filter(r => r.locked);
    // Practised but not scored for a recording problem (cut off, too quiet):
    // listed and counted as practised, left out of the average (Leanne, 20 Oct
    // 2026: "2 phrases practised" after doing 4).
    const notScored = results.filter(r => r.score == null && !r.locked).length;
    const avg = scored.length
        ? Math.round(scored.reduce((s, r) => s + r.score, 0) / scored.length)
        : null;
    const lowScores = scored.filter(r => r.score < REDO_BELOW);
    const lockedMessage = unscored.find(r => r.lockedMessage)?.lockedMessage || '';
    // Collect Speechace phoneme tips across the session, deduped by (word,expected).
    const phoneTips = [];
    const seenPhone = new Set();
    for (const r of results) {
        for (const t of (r.phone_tips || [])) {
            const key = `${(t.word || '').toLowerCase()}|${t.expected}`;
            if (seenPhone.has(key)) continue;
            seenPhone.add(key);
            // Carry the phrase along so the speaker button can play the word in
            // context here too, rather than stranded on its own.
            // Each tip remembers which phrase it came from: the summary
            // spans several, so one id on the component would be wrong
            // for most of them.
            // The word's own score from that phrase's result, so the card shows
            // the same number the phrase screen did, not an average of the weak
            // sounds in it (which read far lower).
            const own = (r.word_scores || []).find(w => occKey(w.word, occOf(w)) === occKey(t.word, occOf(t)));
            // One card per word for the whole session: every attempt is filed
            // as the first, so "les" from three phrases is one card with all
            // its sounds, carrying its lowest score (Leanne, 18 Oct 2026).
            const score = own ? own.score : undefined;
            const already = phoneTips.find(p => normWordKey(p.word) === normWordKey(t.word));
            if (already && score != null && (already.word_score == null || score < already.word_score)) {
                for (const p of phoneTips) if (normWordKey(p.word) === normWordKey(t.word)) p.word_score = score;
            }
            phoneTips.push({ ...t, occurrence: 1, phraseId: r.phrase?.id,
                             word_score: already ? already.word_score : score });
        }
    }
    const stressTips = [];
    const seenStress = new Set();
    for (const r of results) {
        for (const t of (r.stress_tips || [])) {
            const key = `${(t.word || '').toLowerCase()}|${t.syllable}`;
            if (seenStress.has(key)) continue;
            seenStress.add(key);
            stressTips.push({ ...t, occurrence: 1, phraseId: r.phrase?.id });
        }
    }
    // Gemini's per-word explanations, deduped the same way — always collected
    // (not just when Speechace produced nothing), since PronunciationTips now
    // merges these onto the matching word's card instead of using them only
    // as a fallback.
    const issueTips = [];
    const seenIssue = new Set();
    for (const r of results) {
        for (const it of (r.issues || [])) {
            const tip = (it && it.tip || '').trim();
            const key = `${(it.word || '').toLowerCase()}|${tip}`;
            if (!tip || seenIssue.has(key)) continue;
            seenIssue.add(key);
            issueTips.push({ ...it, phraseId: r.phrase?.id });
        }
    }
    return (
        <>
            {/* Day 30 is the challenge's last day before the badge clears itself
                on day 31, so a session finished here is the one to mark. Says
                nothing about a prize, that stays Leanne's call, made by hand from
                the admin panel once she's checked how much of the month they did. */}
            {challengeDay === 30 && (
                <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center max-w-md mx-auto">
                    <p className="text-sm font-bold text-amber-800 inline-flex items-center gap-1.5">
                        <Trophy className="h-4 w-4 fill-current text-amber-500" />
                        Well done{firstName ? `, ${firstName},` : ''} for completing the 30-day challenge! 🎉
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                        30 days of shadowing, in the books.
                    </p>
                </div>
            )}
            <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-foreground mb-1">
                    {avg === 100 ? 'Perfect Session!' : 'Session Complete!'}
                </h2>
                <p className="text-sm text-stone-500 mb-4">
                    {results.length} {results.length === 1 ? 'phrase' : 'phrases'} practised
                    {notScored > 0 ? ` · ${notScored} not scored` : ''}
                    {avg != null ? ' · average score' : ''}
                </p>
                {avg != null ? (
                    <>
                        <div className="inline-flex flex-col items-center gap-2 mb-1">
                            <ScoreCircle score={avg} suffix="%" />
                        </div>
                        <p className={`text-xl font-semibold ${getScoreColor(avg)}`}>
                            {avg === 100 ? 'Perfect!' : getScoreLabel(avg)}
                        </p>
                        {avg === 100 && (
                            <p className="text-base font-bold text-amber-500 mt-2 animate-pulse">Flawless pronunciation, bravo!</p>
                        )}
                    </>
                ) : (
                    // Nothing scored this session. The end of a session is the
                    // natural moment to say what a paid plan would have told them.
                    <div className="inline-flex flex-col items-center gap-2 mb-1">
                        <NoScore onClick={() => setUpgradeOpen(true)} />
                        <NoScoreCaption onUpgrade={() => setUpgradeOpen(true)} />
                    </div>
                )}
            </div>

            <div className="space-y-2 max-w-md mx-auto mb-2">
                {/* Worst first, and the ones below 60 marked to redo, so the
                    phrases the line below talks about are the obvious ones
                    (Leanne, 18 Oct 2026). Unscored attempts go last. */}
                {[...results].sort((a, b) => (a.score ?? 101) - (b.score ?? 101)).map((r, i) => (
                    <div
                        key={i}
                        className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border ${getScoreRowClasses(r.score)}`}
                        data-testid="summary-row"
                    >
                        <span className="text-sm text-foreground truncate flex-1 min-w-0">{r.phrase.french}</span>
                        {r.score != null && r.score < REDO_BELOW && (
                            <span className={`text-[11px] font-semibold uppercase tracking-wide shrink-0 ${getScoreColor(r.score)}`}>Redo</span>
                        )}
                        {r.locked
                            ? <span className="text-xs font-semibold shrink-0 text-amber-700">No score</span>
                            : r.score == null
                                ? <span className="text-xs font-semibold shrink-0 text-stone-500">Not scored: recording problem</span>
                                : <span className={`text-sm font-bold shrink-0 ${getScoreColor(r.score)}`}>{Math.round(r.score)}%</span>}
                    </div>
                ))}
            </div>

            {lowScores.length > 0 && (
                <div className="text-left mt-5 max-w-md mx-auto space-y-1.5">
                    <p className="text-base font-semibold text-foreground">
                        {lowScores.length} {lowScores.length === 1 ? 'phrase' : 'phrases'} scored below 60%. Redo just those to focus your practice.
                    </p>
                    <p className="text-sm text-stone-500">
                        Have a look at the word and sound tips below first, then redo the phrases with those in mind.
                    </p>
                </div>
            )}

            {(phoneTips.length > 0 || stressTips.length > 0 || issueTips.length > 0 || scoreLocked) && (
                <div className="max-w-md mx-auto mt-6" data-testid="session-tips">
                    <PronunciationTips
                        phoneTips={phoneTips}
                        stressTips={stressTips}
                        issues={issueTips}
                        title="Tips from this session"
                        scoreLocked={scoreLocked}
                        worstFirst
                        hideGood
                        showLessons
                    />
                </div>
            )}
            <UpgradeDialog
                open={upgradeOpen}
                onOpenChange={setUpgradeOpen}
                title="Pronunciation scoring is a paid feature"
                message={lockedMessage}
                showOpinionBox
            />
        </>
    );
}
